import { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { AppContext } from 'contexts/AppContext';
import { YES_NO_OPTIONS } from 'utils/constants';
import { isMinor as checkIsMinor } from 'pages/AffordaHousehold/utils';

const GridProps = {
  xs: 12,
  sm: 6,
};

export const NOT_WISH_TO_PROVIDE = 'notWishToProvide';
export const NOT_WISH_TO_FURNISH = 'notWishToFurnish';
const DISABILITY_VALUES = ['yes', 'no', NOT_WISH_TO_PROVIDE];

export const VOLUNTARY_SELF_IDENTIFICATION_FIELD_NAMES = {
  gender: 'gender',
  hasDisability: 'hasDisability',
  disabilityType: 'disabilityType',
  ethnicity: 'ethnicity',
  veteran: 'veteran',
  race: 'race',
};

const disabilityTypeOptions = [
  {
    label: (
      <FormattedMessage id="afforda.documentation.applicationSubmission.voluntarySelfIdentification.disabilityType.mobile" />
    ),
    value: 'mobile',
  },
  {
    label: (
      <FormattedMessage id="afforda.documentation.applicationSubmission.voluntarySelfIdentification.disabilityType.vision" />
    ),
    value: 'vision',
  },
  {
    label: (
      <FormattedMessage id="afforda.documentation.applicationSubmission.voluntarySelfIdentification.disabilityType.hearing" />
    ),
    value: 'hearing',
  },
  {
    label: (
      <FormattedMessage id="afforda.documentation.applicationSubmission.voluntarySelfIdentification.disabilityType.other" />
    ),
    value: 'other',
  },
];

const customSx = {
  flexWrap: 'wrap',
  width: '100%',
  '& > button': {
    width: { xs: 'calc(50% - 4px)', sm: 'calc(100% - 4px)' },
  },
  '& > button: last-child': {
    width: '100%',
  },
};

const useVoluntarySelfIdentificationFields = ({ formId, member }) => {
  const intl = useIntl();
  const { genders, ethnicities, races } = useContext(AppContext);

  const voluntarySelfIdentificationFields = useMemo(() => {
    const memberName = member?.firstName;
    const isMinor = checkIsMinor(member);
    const genderOptions = genders?.map((g) => ({
      label: intl.formatMessage({
        id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.gender.${g.value}`,
      }),
      value: g.value,
    }));

    const ethnicityOptions = ethnicities?.map((e) => ({
      label: intl.formatMessage({
        id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.ethnicity.${e.value}`,
      }),
      value: e.value,
    }));

    const disabilityOptions = DISABILITY_VALUES.map((d) => ({
      label: intl.formatMessage({
        id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.hasDisability.${d}`,
      }),
      value: d,
    }));

    const racesOptions = races
      ?.filter((r) => r.value !== NOT_WISH_TO_PROVIDE)
      ?.map((r) => ({
        text: intl.formatMessage({
          id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.race.${r.value}`,
        }),
        value: r.value,
      }));
    return [
      {
        name: VOLUNTARY_SELF_IDENTIFICATION_FIELD_NAMES.gender,
        label: intl.formatMessage(
          {
            id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.gender`,
          },
          { name: memberName },
        ),
        variant: 'buttonGroup',
        options: genderOptions,
        GridProps,
        sx: customSx,
        direction: 'row',
      },
      {
        name: VOLUNTARY_SELF_IDENTIFICATION_FIELD_NAMES.hasDisability,
        label: intl.formatMessage(
          {
            id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.hasDisability`,
          },
          { name: memberName },
        ),
        variant: 'buttonGroup',
        options: disabilityOptions,
        GridProps,
        sx: customSx,
        direction: 'row',
      },
      {
        name: VOLUNTARY_SELF_IDENTIFICATION_FIELD_NAMES.disabilityType,
        multiselect: true,
        label: intl.formatMessage({
          id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.disabilityType`,
        }),
        variant: 'buttonGroup',
        hidden: ({ getValues }) =>
          getValues(VOLUNTARY_SELF_IDENTIFICATION_FIELD_NAMES.hasDisability) !==
          'yes',
        options: disabilityTypeOptions,
        helperText: intl.formatMessage({
          id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.disabilityType.helperText`,
        }),
        GridProps,
        sx: { ...customSx, '& > button: last-child': false },
        direction: 'row',
      },
      {
        name: VOLUNTARY_SELF_IDENTIFICATION_FIELD_NAMES.ethnicity,
        label: intl.formatMessage(
          {
            id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.ethnicity`,
          },
          { name: memberName },
        ),
        variant: 'buttonGroup',
        options: ethnicityOptions,
        GridProps,
        sx: customSx,
        direction: 'row',
      },
      {
        name: VOLUNTARY_SELF_IDENTIFICATION_FIELD_NAMES.veteran,
        label: intl.formatMessage(
          {
            id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.veteran`,
          },
          { name: memberName },
        ),
        variant: 'buttonGroup',
        options: YES_NO_OPTIONS,
        hidden: isMinor,
        GridProps,
        direction: 'row',
      },
      {
        name: VOLUNTARY_SELF_IDENTIFICATION_FIELD_NAMES.race,
        label: intl.formatMessage(
          {
            id: `afforda.documentation.applicationSubmission.voluntarySelfIdentification.race`,
          },
          { name: memberName },
        ),
        noPreferenceOptionEnabled: true,
        noPreferenceOption: {
          text: intl.formatMessage({ id: 'app.option.notWishToProvide' }),
          value: NOT_WISH_TO_FURNISH,
        },
        variant: 'multiSelect',
        options: racesOptions,
        GridProps,
        direction: 'row',
      },
    ].map((field) => ({ ...field, id: formId }));
  }, [ethnicities, formId, genders, intl, member, races]);

  return { voluntarySelfIdentificationFields };
};

export default useVoluntarySelfIdentificationFields;
